import { API_URL } from "../../constantes/constantes";
import { replaceAllLongIds } from "./idMapper";
import { replaceCountryIds } from "./idMapperCountry";
import { replaceEliminatoriaIds } from "./idMapperEliminatorias";

// DESPUÉS:
let OFFICIAL_RESULTS = {};
let OFFICIAL_GROUP_RESULTS = {};
let OFFICIAL_GROUP_STANDINGS = {};

let _loaded = false;

// Función helper para reemplazar nombres de países en toda la data
function replaceUSA(obj) {
  if (typeof obj === 'string') {
    return obj
      .replace(/Estados Unidos/g, 'EE.UU.')
      .replace(/Islas de Cabo Verde/g, 'Cabo Verde');
  }
  if (Array.isArray(obj)) {
    return obj.map(item => replaceUSA(item));
  }
  if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      newObj[key] = replaceUSA(obj[key]);
    }
    return newObj;
  }
  return obj;
}

export async function loadOfficialResults() {
  if (_loaded) return; // evitar fetch duplicado
  try {
    // const res = await fetch(`${API_URL}mundial2026/officialResult_v2.json`);
    const res = await fetch(`${API_URL}mundial2026/officialResults.json`);
    // const res = await fetch(`/results/data/officialResults.json`);
    const data = await res.json();
    
    // Reemplazar nombres de países en todos los arreglos
    let cleanedData = replaceUSA(data);
    
    // Aplicar transformación de IDs largos a sus equivalentes (A1, A2, etc.)
    let transformedData = replaceAllLongIds(cleanedData);
    
    // Aplicar transformación de IDs de países a sus nombres equivalentes
    // y transformación de IDs de eliminatorias a sus números equivalentes
    transformedData = {
      ...transformedData,
      OFFICIAL_GROUP_STANDINGS: replaceCountryIds(transformedData.OFFICIAL_GROUP_STANDINGS || {}),
      OFFICIAL_RESULTS: replaceEliminatoriaIds(transformedData.OFFICIAL_RESULTS || {})
    };
    
    // console.log(" data original: ", data);
    // console.log(" data transformada: ", transformedData);
        
    OFFICIAL_RESULTS = transformedData.OFFICIAL_RESULTS || {};
    OFFICIAL_GROUP_RESULTS = transformedData.OFFICIAL_GROUP_RESULTS || {};
    OFFICIAL_GROUP_STANDINGS = transformedData.OFFICIAL_GROUP_STANDINGS || {};
    _loaded = true;

    // console.log(" resultados de grupos oficiales: ", OFFICIAL_GROUP_RESULTS)
    // console.log(" estadisticas: ", OFFICIAL_GROUP_STANDINGS)
  } catch (err) {
    console.error("Error cargando resultados oficiales:", err);
  }
}

// Helper para obtener datos de un resultado oficial
export const getOfficialResult = (matchNumber) => {
  const entry = OFFICIAL_RESULTS[matchNumber];
  if (!entry) return null;
  // Soportar formato objeto { winner, homeScore, awayScore }
  if (typeof entry === "object") return entry;
  // Soportar formato string legacy (solo winner)
  return { winner: entry };
};

// standings.js
const toIntOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Helper para inicializar simulationState en un grupo
export const initializeGroupSimulationState = (group) => {
  const applyOfficialScores = (days) =>
    days.map((day) => ({
      ...day,
      matches: day.matches.map((match) => {
        const official = OFFICIAL_GROUP_RESULTS[match.id];
        if (official) {
          return {
            ...match,
            homeScore: official.homeScore,
            awayScore: official.awayScore,
            locked: true,
          };
        }
        return match;
      }),
    }));

  return {
    ...group,
    days: applyOfficialScores(group.days),
    simulationState: group.simulationState || {
      isSimulated: false,
      simulatedAt: null,
      simulationMethod: null // "auto" | "manual" | null
    }
  };
};

// Helper para marcar un grupo como simulado
export const markGroupAsSimulated = (group, method = "manual") => {
  return {
    ...group,
    simulationState: {
      isSimulated: true,
      simulatedAt: new Date().toISOString(),
      simulationMethod: method
    }
  };
};

// ============================================================================
// FUERZA RELATIVA DE EQUIPOS (0–100)
// Influye en la probabilidad de ganar partidos simulados.
// Un equipo con 90 vs uno con 50 tendrá ~73% de probabilidad de ganar.
// ============================================================================
export const teamStrength = {
  // Top 11 — basado en ranking FIFA actual
  "Francia": 98,       // #1  — 1877 pts
  "España": 97,        // #2  — 1876 pts
  "Argentina": 96,     // #3  — 1874 pts
  "Inglaterra": 95,    // #4  — 1825 pts
  "Portugal": 94,      // #5  — 1763 pts
  "Brasil": 93,        // #6  — 1761 pts
  "Países Bajos": 92,  // #7  — 1757 pts
  "Marruecos": 92,     // #8  — 1755 pts
  "Bélgica": 91,       // #9  — 1734 pts
  "Alemania": 90,      // #10 — 1730 pts
  "Croacia": 90,       // #11 — 1717 pts
  // Contendientes fuertes (~12–20)
  "Colombia": 84,
  "Japón": 82,
  "Uruguay": 81,
  "EE.UU.": 81,
  "México": 80,
  "Senegal": 79,
  "Suiza": 78,
  "Ecuador": 72,
  "RI de Irán": 74,
  // Competitivos (~20–30)
  "Turquía": 30,
  "Austria": 30,
  "Suecia": 30,
  "Corea": 30,
  "Noruega": 30,
  "Egipto": 30,
  "Australia": 29,
  "C. de Marfil": 29,
  "Argelia": 29,
  "Canadá": 29,
  "Paraguay": 29,
  "Escocia": 29,
  // Medios (~30–40)
  "República Checa": 29,
  "Ghana": 29,
  "Túnez": 29,
  "Bosnia": 29,
  "Arabia Saudí": 29,
  "Sudáfrica": 29,
  "RD Congo": 28,
  "Panamá": 28,
  // Modestos
  "Irak": 28,
  "Uzbekistán": 28,
  "Jordania": 28,
  "Cabo Verde": 28,
  "Qatar": 28,
  "Nueva Zelanda": 28,
  "Haití": 28,
  "Curazao": 28,
};

const DEFAULT_STRENGTH = 65;

/**
 * Calcula la probabilidad de que el equipo "home" gane contra "away"
 * basándose en la fuerza relativa de ambos.
 * Usa exponente para amplificar la ventaja de los favoritos.
 * Retorna un valor entre 0.15 y 0.9.
 */
function getWinProbability(homeTeam, awayTeam) {
  const sH = teamStrength[homeTeam] ?? DEFAULT_STRENGTH;
  const sA = teamStrength[awayTeam] ?? DEFAULT_STRENGTH;
  // Elevar al cubo para amplificar diferencias: favoritos dominan más
  const raw = Math.pow(sH, 3) / (Math.pow(sH, 3) + Math.pow(sA, 3));
  // Clamp entre 0.15 y 0.9: siempre hay chance de sorpresa, pero mínima
  return Math.min(0.9, Math.max(0.15, raw));
}

/**
 * Genera un marcador simulado influenciado por la fuerza de los equipos.
 * El equipo más fuerte tiende a meter más goles.
 */
function simulateMatchScore(homeTeam, awayTeam) {
  const sH = teamStrength[homeTeam] ?? DEFAULT_STRENGTH;
  const sA = teamStrength[awayTeam] ?? DEFAULT_STRENGTH;
  // Lambda de Poisson: favoritos (95) esperan ~2.5 goles, débiles (48) esperan ~0.7
  // Rango más amplio para que la diferencia se note en el marcador
  const lambdaHome = 0.4 + (sH / 100) * 2.2;
  const lambdaAway = 0.4 + (sA / 100) * 2.2;
  const homeScore = poissonRandom(lambdaHome);
  const awayScore = poissonRandom(lambdaAway);
  return { homeScore: String(homeScore), awayScore: String(awayScore) };
}

/**
 * Genera un número aleatorio con distribución de Poisson.
 * Más realista que Math.floor(Math.random() * 5) para goles de fútbol.
 */
function poissonRandom(lambda) {
  let L = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= Math.random();
  } while (p > L);
  return Math.min(k - 1, 5); // Cap en 5 goles máximo
}

// Mapeo de nombres de equipos a códigos de país
export const teamFlagMap = {
  "México": "MEX",
  "Sudáfrica": "ZAF",
  "Corea": "KOR",
  "Bosnia": "BIH",
  "Bosnia-Herzegovina": "BIH",
  "República de Corea": "KOR",
  "Canadá": "CAN",
  "Turquía": "TUR",
  "Qatar": "QAT",
  "Suiza": "SUI",
  "Brasil": "BRA",
  "Marruecos": "MAR",
  "Haití": "HAI",
  "Escocia": "SCO",
  "EE.UU.": "USA",
  "Australia": "AUS",
  "Paraguay": "PAR",
  "República Checa": "CZE",
  "Chequia": "CZE",
  "Alemania": "GER",
  "Ecuador": "ECU",
  "C. de Marfil": "CIV",
  "Curazao": "CUW",
  "Países Bajos": "NED",
  "Japón": "JPN",
  "Túnez": "TUN",
  "Suecia": "SWE",
  "Bélgica": "BEL",
  "RI de Irán": "IRN",
  "Egipto": "EGY",
  "Nueva Zelanda": "NZL",
  "España": "ESP",
  "Uruguay": "URU",
  "Arabia Saudí": "KSA",
  "Cabo Verde": "CPV",
  "Francia": "FRA",
  "Senegal": "SEN",
  "Noruega": "NOR",
  "Irak": "IRQ",
  "Argentina": "ARG",
  "Austria": "AUT",
  "Argelia": "ALG",
  "Jordania": "JOR",
  "Portugal": "POR",
  "Colombia": "COL",
  "Uzbekistán": "UZB",
  "RD Congo": "COD",
  "Inglaterra": "ENG",
  "Croacia": "CRO",
  "Panamá": "PAN",
  "Ghana": "GHA",
  "Costa de Marfil": "CIV"
};

/**
 * Genera un número pseudoaleatorio determinista a partir de una semilla entera.
 *
 * Implementa el algoritmo Mulberry32, un PRNG (Pseudo-Random Number Generator)
 * de 32 bits con buena distribución estadística y bajo costo computacional.
 *
 * La misma semilla siempre produce el mismo resultado, lo que permite
 * reproducir simulaciones de forma consistente.
 */
function seededRandom(seed) {
  // Mulberry32 — PRNG rápido con buena distribución
  let t = (seed | 0) + 0x6D2B79F5 | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/**
 * Convierte una cadena de texto en un entero no negativo de 32 bits (hash).
 *
 * Implementa el algoritmo djb2-like (variante con `hash * 31 + char`),
 * ampliamente usado por su simplicidad y distribución razonable.
 * El operador `hash & hash` fuerza el truncado a entero de 32 bits en cada
 * iteración, evitando desbordamiento de punto flotante.
 *
 * Uso típico: generar una semilla numérica a partir de un identificador de
 * partido (string) para pasarla a `seededRandom` y obtener resultados
 * deterministas por partido.
 */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // truncar a 32 bits
  }
  return Math.abs(hash);
}

export function computeStandings(group, groupId) {
  // Si hay standings oficiales para este grupo, usarlos solo si todos los partidos ya se jugaron
  if (groupId && OFFICIAL_GROUP_STANDINGS[groupId]) {
    const matchIds = ["1","2","3","4","5","6"].map(n => `${groupId}${n}`);
    const allPlayed = matchIds.every(id => OFFICIAL_GROUP_RESULTS[id]);
    if (allPlayed) {
      const officialRows = OFFICIAL_GROUP_STANDINGS[groupId];
      return officialRows.map((row, i) => ({
        ...row,
        pos: i + 1,
        flag: teamFlagMap[row.team] || "DEN",
      }));
    }
  }

  const table = new Map();
  group.teams.forEach((team) => {
    table.set(team, { team, flag: teamFlagMap[team] || "DEN", pj: 0, g: 0, e: 0, p: 0, gf: 0, gc: 0, diff: 0, pts: 0, fairPlay: 0 });
  });

  group.days.forEach((day) => {
    day.matches.forEach((m) => {
      const hs = toIntOrNull(m.homeScore);
      const as = toIntOrNull(m.awayScore);
      if (hs === null || as === null) return;
      const home = table.get(m.home);
      const away = table.get(m.away);
      if (!home || !away) return;
      home.pj += 1; away.pj += 1;
      home.gf += hs; home.gc += as;
      away.gf += as; away.gc += hs;
      if (hs > as) { home.g += 1; away.p += 1; home.pts += 3; }
      else if (hs < as) { away.g += 1; home.p += 1; away.pts += 3; }
      else { home.e += 1; away.e += 1; home.pts += 1; away.pts += 1; }
      if (m.homeFairPlay !== undefined) home.fairPlay += m.homeFairPlay;
      if (m.awayFairPlay !== undefined) away.fairPlay += m.awayFairPlay;
    });
  });

  // Construir mapa de duelos directos
  const headToHead = new Map();
  group.days.forEach((day) => {
    day.matches.forEach((m) => {
      const hs = toIntOrNull(m.homeScore);
      const as = toIntOrNull(m.awayScore);
      if (hs === null || as === null) return;
      const key = `${m.home}|${m.away}`;
      headToHead.set(key, { homeGoals: hs, awayGoals: as });
    });
  });

  const getH2HResult = (teamA, teamB) => {
    const ab = headToHead.get(`${teamA}|${teamB}`);
    if (ab) return ab.homeGoals - ab.awayGoals;
    const ba = headToHead.get(`${teamB}|${teamA}`);
    if (ba) return ba.awayGoals - ba.homeGoals;
    return 0;
  };

  const rows = Array.from(table.values()).map((r) => ({ ...r, diff: r.gf - r.gc }));
  const groupSeed = hashString(group.teams.join(''));
  rows.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    const h2h = getH2HResult(a.team, b.team);
    if (h2h !== 0) return h2h > 0 ? -1 : 1;
    if (b.diff !== a.diff) return b.diff - a.diff;
    if (b.gf !== a.gf) return b.gf - a.gf;
    if (a.fairPlay !== b.fairPlay) return a.fairPlay - b.fairPlay;
    return seededRandom(groupSeed + hashString(b.team)) - seededRandom(groupSeed + hashString(a.team));
  });
  return rows.map((r, i) => ({ pos: i + 1, ...r }));
}

// ============================================================================
// ESCENARIOS FIFA PARA TERCEROS LUGARES - MUNDIAL 2026 (48 equipos, 12 grupos)
// ============================================================================
// Partidos que reciben terceros: 74, 77, 79, 80, 81, 82, 85, 87
// Cada escenario es una combinación de 8 grupos ordenados alfabéticamente
// que define qué tercero va a qué partido.
//
// Primero de grupo que recibe al tercero -> número de partido:
// P79: 1A    | P85: 1B   | P81: 1D   | P74: 1E
// P82: 1G    | P77: 1I   | P87: 1K   | P80: 1L
//
// La asignación concreta de cada tercero proviene de la tabla oficial FIFA
// (Anexo C del Reglamento del Mundial 2026), transcrita en THIRD_PLACE_RAW.

// Tabla oficial FIFA (Anexo C del Reglamento del Mundial 2026):
// "Combinaciones de las ocho mejores terceras". Son las 495 combinaciones
// posibles (C(12,8) = 495) de los 8 grupos que aportan tercer lugar clasificado.
//
// THIRD_PLACE_RAW: cada fila contiene los 8 terceros en el ORDEN DE COLUMNAS
// del reglamento -> [1A, 1B, 1D, 1E, 1G, 1I, 1K, 1L].
// Cada letra es el grupo del tercero que enfrenta a ese primero de grupo.
//
// Equivalencia primero de grupo -> número de partido (Dieciseisavos / Round of 32):
//   1A=79, 1B=85, 1D=81, 1E=74, 1G=82, 1I=77, 1K=87, 1L=80
const THIRD_PLACE_FIRST_ORDER = ["A", "B", "D", "E", "G", "I", "K", "L"];
const THIRD_PLACE_MATCH_BY_FIRST = { A: 79, B: 85, D: 81, E: 74, G: 82, I: 77, K: 87, L: 80 };

const THIRD_PLACE_RAW = [
  ["E","J","I","F","H","G","L","K"], // 1
  ["H","G","I","D","J","F","L","K"], // 2
  ["E","J","I","D","H","G","L","K"], // 3
  ["E","J","I","D","H","F","L","K"], // 4
  ["E","G","I","D","J","F","L","K"], // 5
  ["E","G","J","D","H","F","L","K"], // 6
  ["E","G","I","D","H","F","L","K"], // 7
  ["E","G","J","D","H","F","L","I"], // 8
  ["E","G","J","D","H","F","I","K"], // 9
  ["H","G","I","C","J","F","L","K"], // 10
  ["E","J","I","C","H","G","L","K"], // 11
  ["E","J","I","C","H","F","L","K"], // 12
  ["E","G","I","C","J","F","L","K"], // 13
  ["E","G","J","C","H","F","L","K"], // 14
  ["E","G","I","C","H","F","L","K"], // 15
  ["E","G","J","C","H","F","L","I"], // 16
  ["E","G","J","C","H","F","I","K"], // 17
  ["H","G","I","C","J","D","L","K"], // 18
  ["C","J","I","D","H","F","L","K"], // 19
  ["C","G","I","D","J","F","L","K"], // 20
  ["C","G","J","D","H","F","L","K"], // 21
  ["C","G","I","D","H","F","L","K"], // 22
  ["C","G","J","D","H","F","L","I"], // 23
  ["C","G","J","D","H","F","I","K"], // 24
  ["E","J","I","C","H","D","L","K"], // 25
  ["E","G","I","C","J","D","L","K"], // 26
  ["E","G","J","C","H","D","L","K"], // 27
  ["E","G","I","C","H","D","L","K"], // 28
  ["E","G","J","C","H","D","L","I"], // 29
  ["E","G","J","C","H","D","I","K"], // 30
  ["C","J","E","D","I","F","L","K"], // 31
  ["C","J","E","D","H","F","L","K"], // 32
  ["C","E","I","D","H","F","L","K"], // 33
  ["C","J","E","D","H","F","L","I"], // 34
  ["C","J","E","D","H","F","I","K"], // 35
  ["C","G","E","D","J","F","L","K"], // 36
  ["C","G","E","D","I","F","L","K"], // 37
  ["C","G","E","D","J","F","L","I"], // 38
  ["C","G","E","D","J","F","I","K"], // 39
  ["C","G","E","D","H","F","L","K"], // 40
  ["C","G","J","D","H","F","L","E"], // 41
  ["C","G","J","D","H","F","E","K"], // 42
  ["C","G","E","D","H","F","L","I"], // 43
  ["C","G","E","D","H","F","I","K"], // 44
  ["C","G","J","D","H","F","E","I"], // 45
  ["H","J","B","F","I","G","L","K"], // 46
  ["E","J","I","B","H","G","L","K"], // 47
  ["E","J","B","F","I","H","L","K"], // 48
  ["E","J","B","F","I","G","L","K"], // 49
  ["E","J","B","F","H","G","L","K"], // 50
  ["E","G","B","F","I","H","L","K"], // 51
  ["E","J","B","F","H","G","L","I"], // 52
  ["E","J","B","F","H","G","I","K"], // 53
  ["H","J","B","D","I","G","L","K"], // 54
  ["H","J","B","D","I","F","L","K"], // 55
  ["I","G","B","D","J","F","L","K"], // 56
  ["H","G","B","D","J","F","L","K"], // 57
  ["H","G","B","D","I","F","L","K"], // 58
  ["H","G","B","D","J","F","L","I"], // 59
  ["H","G","B","D","J","F","I","K"], // 60
  ["E","J","B","D","I","H","L","K"], // 61
  ["E","J","B","D","I","G","L","K"], // 62
  ["E","J","B","D","H","G","L","K"], // 63
  ["E","G","B","D","I","H","L","K"], // 64
  ["E","J","B","D","H","G","L","I"], // 65
  ["E","J","B","D","H","G","I","K"], // 66
  ["E","J","B","D","I","F","L","K"], // 67
  ["E","J","B","D","H","F","L","K"], // 68
  ["E","I","B","D","H","F","L","K"], // 69
  ["E","J","B","D","H","F","L","I"], // 70
  ["E","J","B","D","H","F","I","K"], // 71
  ["E","G","B","D","J","F","L","K"], // 72
  ["E","G","B","D","I","F","L","K"], // 73
  ["E","G","B","D","J","F","L","I"], // 74
  ["E","G","B","D","J","F","I","K"], // 75
  ["E","G","B","D","H","F","L","K"], // 76
  ["H","G","B","D","J","F","L","E"], // 77
  ["H","G","B","D","J","F","E","K"], // 78
  ["E","G","B","D","H","F","L","I"], // 79
  ["E","G","B","D","H","F","I","K"], // 80
  ["H","G","B","D","J","F","E","I"], // 81
  ["H","J","B","C","I","G","L","K"], // 82
  ["H","J","B","C","I","F","L","K"], // 83
  ["I","G","B","C","J","F","L","K"], // 84
  ["H","G","B","C","J","F","L","K"], // 85
  ["H","G","B","C","I","F","L","K"], // 86
  ["H","G","B","C","J","F","L","I"], // 87
  ["H","G","B","C","J","F","I","K"], // 88
  ["E","J","B","C","I","H","L","K"], // 89
  ["E","J","B","C","I","G","L","K"], // 90
  ["E","J","B","C","H","G","L","K"], // 91
  ["E","G","B","C","I","H","L","K"], // 92
  ["E","J","B","C","H","G","L","I"], // 93
  ["E","J","B","C","H","G","I","K"], // 94
  ["E","J","B","C","I","F","L","K"], // 95
  ["E","J","B","C","H","F","L","K"], // 96
  ["E","I","B","C","H","F","L","K"], // 97
  ["E","J","B","C","H","F","L","I"], // 98
  ["E","J","B","C","H","F","I","K"], // 99
  ["E","G","B","C","J","F","L","K"], // 100
  ["E","G","B","C","I","F","L","K"], // 101
  ["E","G","B","C","J","F","L","I"], // 102
  ["E","G","B","C","J","F","I","K"], // 103
  ["E","G","B","C","H","F","L","K"], // 104
  ["H","G","B","C","J","F","L","E"], // 105
  ["H","G","B","C","J","F","E","K"], // 106
  ["E","G","B","C","H","F","L","I"], // 107
  ["E","G","B","C","H","F","I","K"], // 108
  ["H","G","B","C","J","F","E","I"], // 109
  ["H","J","B","C","I","D","L","K"], // 110
  ["I","G","B","C","J","D","L","K"], // 111
  ["H","G","B","C","J","D","L","K"], // 112
  ["H","G","B","C","I","D","L","K"], // 113
  ["H","G","B","C","J","D","L","I"], // 114
  ["H","G","B","C","J","D","I","K"], // 115
  ["C","J","B","D","I","F","L","K"], // 116
  ["C","J","B","D","H","F","L","K"], // 117
  ["C","I","B","D","H","F","L","K"], // 118
  ["C","J","B","D","H","F","L","I"], // 119
  ["C","J","B","D","H","F","I","K"], // 120
  ["C","G","B","D","J","F","L","K"], // 121
  ["C","G","B","D","I","F","L","K"], // 122
  ["C","G","B","D","J","F","L","I"], // 123
  ["C","G","B","D","J","F","I","K"], // 124
  ["C","G","B","D","H","F","L","K"], // 125
  ["C","G","B","D","H","F","L","J"], // 126
  ["H","G","B","C","J","F","D","K"], // 127
  ["C","G","B","D","H","F","L","I"], // 128
  ["C","G","B","D","H","F","I","K"], // 129
  ["H","G","B","C","J","F","D","I"], // 130
  ["E","J","B","C","I","D","L","K"], // 131
  ["E","J","B","C","H","D","L","K"], // 132
  ["E","I","B","C","H","D","L","K"], // 133
  ["E","J","B","C","H","D","L","I"], // 134
  ["E","J","B","C","H","D","I","K"], // 135
  ["E","G","B","C","J","D","L","K"], // 136
  ["E","G","B","C","I","D","L","K"], // 137
  ["E","G","B","C","J","D","L","I"], // 138
  ["E","G","B","C","J","D","I","K"], // 139
  ["E","G","B","C","H","D","L","K"], // 140
  ["H","G","B","C","J","D","L","E"], // 141
  ["H","G","B","C","J","D","E","K"], // 142
  ["E","G","B","C","H","D","L","I"], // 143
  ["E","G","B","C","H","D","I","K"], // 144
  ["H","G","B","C","J","D","E","I"], // 145
  ["C","J","B","D","E","F","L","K"], // 146
  ["C","E","B","D","I","F","L","K"], // 147
  ["C","J","B","D","E","F","L","I"], // 148
  ["C","J","B","D","E","F","I","K"], // 149
  ["C","E","B","D","H","F","L","K"], // 150
  ["C","J","B","D","H","F","L","E"], // 151
  ["C","J","B","D","H","F","E","K"], // 152
  ["C","E","B","D","H","F","L","I"], // 153
  ["C","E","B","D","H","F","I","K"], // 154
  ["C","J","B","D","H","F","E","I"], // 155
  ["C","G","B","D","E","F","L","K"], // 156
  ["C","G","B","D","J","F","L","E"], // 157
  ["C","G","B","D","J","F","E","K"], // 158
  ["C","G","B","D","E","F","L","I"], // 159
  ["C","G","B","D","E","F","I","K"], // 160
  ["C","G","B","D","J","F","E","I"], // 161
  ["C","G","B","D","H","F","L","E"], // 162
  ["C","G","B","D","H","F","E","K"], // 163
  ["H","G","B","C","J","F","D","E"], // 164
  ["C","G","B","D","H","F","E","I"], // 165
  ["H","J","I","F","A","G","L","K"], // 166
  ["E","J","I","A","H","G","L","K"], // 167
  ["E","J","I","F","A","H","L","K"], // 168
  ["E","J","I","F","A","G","L","K"], // 169
  ["E","G","J","F","A","H","L","K"], // 170
  ["E","G","I","F","A","H","L","K"], // 171
  ["E","G","J","F","A","H","L","I"], // 172
  ["E","G","J","F","A","H","I","K"], // 173
  ["H","J","I","D","A","G","L","K"], // 174
  ["H","J","I","D","A","F","L","K"], // 175
  ["I","G","J","D","A","F","L","K"], // 176
  ["H","G","J","D","A","F","L","K"], // 177
  ["H","G","I","D","A","F","L","K"], // 178
  ["H","G","J","D","A","F","L","I"], // 179
  ["H","G","J","D","A","F","I","K"], // 180
  ["E","J","I","D","A","H","L","K"], // 181
  ["E","J","I","D","A","G","L","K"], // 182
  ["E","G","J","D","A","H","L","K"], // 183
  ["E","G","I","D","A","H","L","K"], // 184
  ["E","G","J","D","A","H","L","I"], // 185
  ["E","G","J","D","A","H","I","K"], // 186
  ["E","J","I","D","A","F","L","K"], // 187
  ["H","J","E","D","A","F","L","K"], // 188
  ["H","E","I","D","A","F","L","K"], // 189
  ["H","J","E","D","A","F","L","I"], // 190
  ["H","J","E","D","A","F","I","K"], // 191
  ["E","G","J","D","A","F","L","K"], // 192
  ["E","G","I","D","A","F","L","K"], // 193
  ["E","G","J","D","A","F","L","I"], // 194
  ["E","G","J","D","A","F","I","K"], // 195
  ["H","G","E","D","A","F","L","K"], // 196
  ["H","G","J","D","A","F","L","E"], // 197
  ["H","G","J","D","A","F","E","K"], // 198
  ["H","G","E","D","A","F","L","I"], // 199
  ["H","G","E","D","A","F","I","K"], // 200
  ["H","G","J","D","A","F","E","I"], // 201
  ["H","J","I","C","A","G","L","K"], // 202
  ["H","J","I","C","A","F","L","K"], // 203
  ["I","G","J","C","A","F","L","K"], // 204
  ["H","G","J","C","A","F","L","K"], // 205
  ["H","G","I","C","A","F","L","K"], // 206
  ["H","G","J","C","A","F","L","I"], // 207
  ["H","G","J","C","A","F","I","K"], // 208
  ["E","J","I","C","A","H","L","K"], // 209
  ["E","J","I","C","A","G","L","K"], // 210
  ["E","G","J","C","A","H","L","K"], // 211
  ["E","G","I","C","A","H","L","K"], // 212
  ["E","G","J","C","A","H","L","I"], // 213
  ["E","G","J","C","A","H","I","K"], // 214
  ["E","J","I","C","A","F","L","K"], // 215
  ["H","J","E","C","A","F","L","K"], // 216
  ["H","E","I","C","A","F","L","K"], // 217
  ["H","J","E","C","A","F","L","I"], // 218
  ["H","J","E","C","A","F","I","K"], // 219
  ["E","G","J","C","A","F","L","K"], // 220
  ["E","G","I","C","A","F","L","K"], // 221
  ["E","G","J","C","A","F","L","I"], // 222
  ["E","G","J","C","A","F","I","K"], // 223
  ["H","G","E","C","A","F","L","K"], // 224
  ["H","G","J","C","A","F","L","E"], // 225
  ["H","G","J","C","A","F","E","K"], // 226
  ["H","G","E","C","A","F","L","I"], // 227
  ["H","G","E","C","A","F","I","K"], // 228
  ["H","G","J","C","A","F","E","I"], // 229
  ["H","J","I","C","A","D","L","K"], // 230
  ["I","G","J","C","A","D","L","K"], // 231
  ["H","G","J","C","A","D","L","K"], // 232
  ["H","G","I","C","A","D","L","K"], // 233
  ["H","G","J","C","A","D","L","I"], // 234
  ["H","G","J","C","A","D","I","K"], // 235
  ["C","J","I","D","A","F","L","K"], // 236
  ["H","J","F","C","A","D","L","K"], // 237
  ["H","F","I","C","A","D","L","K"], // 238
  ["H","J","F","C","A","D","L","I"], // 239
  ["H","J","F","C","A","D","I","K"], // 240
  ["C","G","J","D","A","F","L","K"], // 241
  ["C","G","I","D","A","F","L","K"], // 242
  ["C","G","J","D","A","F","L","I"], // 243
  ["C","G","J","D","A","F","I","K"], // 244
  ["H","G","F","C","A","D","L","K"], // 245
  ["C","G","J","D","A","F","L","H"], // 246
  ["H","G","J","C","A","F","D","K"], // 247
  ["H","G","F","C","A","D","L","I"], // 248
  ["H","G","F","C","A","D","I","K"], // 249
  ["H","G","J","C","A","F","D","I"], // 250
  ["E","J","I","C","A","D","L","K"], // 251
  ["H","J","E","C","A","D","L","K"], // 252
  ["H","E","I","C","A","D","L","K"], // 253
  ["H","J","E","C","A","D","L","I"], // 254
  ["H","J","E","C","A","D","I","K"], // 255
  ["E","G","J","C","A","D","L","K"], // 256
  ["E","G","I","C","A","D","L","K"], // 257
  ["E","G","J","C","A","D","L","I"], // 258
  ["E","G","J","C","A","D","I","K"], // 259
  ["H","G","E","C","A","D","L","K"], // 260
  ["H","G","J","C","A","D","L","E"], // 261
  ["H","G","J","C","A","D","E","K"], // 262
  ["H","G","E","C","A","D","L","I"], // 263
  ["H","G","E","C","A","D","I","K"], // 264
  ["H","G","J","C","A","D","E","I"], // 265
  ["C","J","E","D","A","F","L","K"], // 266
  ["C","E","I","D","A","F","L","K"], // 267
  ["C","J","E","D","A","F","L","I"], // 268
  ["C","J","E","D","A","F","I","K"], // 269
  ["H","E","F","C","A","D","L","K"], // 270
  ["H","J","F","C","A","D","L","E"], // 271
  ["H","J","E","C","A","F","D","K"], // 272
  ["H","E","F","C","A","D","L","I"], // 273
  ["H","E","F","C","A","D","I","K"], // 274
  ["H","J","E","C","A","F","D","I"], // 275
  ["C","G","E","D","A","F","L","K"], // 276
  ["C","G","J","D","A","F","L","E"], // 277
  ["C","G","J","D","A","F","E","K"], // 278
  ["C","G","E","D","A","F","L","I"], // 279
  ["C","G","E","D","A","F","I","K"], // 280
  ["C","G","J","D","A","F","E","I"], // 281
  ["H","G","F","C","A","D","L","E"], // 282
  ["H","G","E","C","A","F","D","K"], // 283
  ["H","G","J","C","A","F","D","E"], // 284
  ["H","G","E","C","A","F","D","I"], // 285
  ["H","J","B","A","I","G","L","K"], // 286
  ["H","J","B","A","I","F","L","K"], // 287
  ["I","J","B","F","A","G","L","K"], // 288
  ["H","J","B","F","A","G","L","K"], // 289
  ["H","G","B","A","I","F","L","K"], // 290
  ["H","J","B","F","A","G","L","I"], // 291
  ["H","J","B","F","A","G","I","K"], // 292
  ["E","J","B","A","I","H","L","K"], // 293
  ["E","J","B","A","I","G","L","K"], // 294
  ["E","J","B","A","H","G","L","K"], // 295
  ["E","G","B","A","I","H","L","K"], // 296
  ["E","J","B","A","H","G","L","I"], // 297
  ["E","J","B","A","H","G","I","K"], // 298
  ["E","J","B","A","I","F","L","K"], // 299
  ["E","J","B","F","A","H","L","K"], // 300
  ["E","I","B","F","A","H","L","K"], // 301
  ["E","J","B","F","A","H","L","I"], // 302
  ["E","J","B","F","A","H","I","K"], // 303
  ["E","J","B","F","A","G","L","K"], // 304
  ["E","G","B","A","I","F","L","K"], // 305
  ["E","J","B","F","A","G","L","I"], // 306
  ["E","J","B","F","A","G","I","K"], // 307
  ["E","G","B","F","A","H","L","K"], // 308
  ["H","J","B","F","A","G","L","E"], // 309
  ["H","J","B","F","A","G","E","K"], // 310
  ["E","G","B","F","A","H","L","I"], // 311
  ["E","G","B","F","A","H","I","K"], // 312
  ["H","J","B","F","A","G","E","I"], // 313
  ["I","J","B","D","A","H","L","K"], // 314
  ["I","J","B","D","A","G","L","K"], // 315
  ["H","J","B","D","A","G","L","K"], // 316
  ["I","G","B","D","A","H","L","K"], // 317
  ["H","J","B","D","A","G","L","I"], // 318
  ["H","J","B","D","A","G","I","K"], // 319
  ["I","J","B","D","A","F","L","K"], // 320
  ["H","J","B","D","A","F","L","K"], // 321
  ["H","I","B","D","A","F","L","K"], // 322
  ["H","J","B","D","A","F","L","I"], // 323
  ["H","J","B","D","A","F","I","K"], // 324
  ["F","J","B","D","A","G","L","K"], // 325
  ["I","G","B","D","A","F","L","K"], // 326
  ["F","J","B","D","A","G","L","I"], // 327
  ["F","J","B","D","A","G","I","K"], // 328
  ["H","G","B","D","A","F","L","K"], // 329
  ["H","G","B","D","A","F","L","J"], // 330
  ["H","G","B","D","A","F","J","K"], // 331
  ["H","G","B","D","A","F","L","I"], // 332
  ["H","G","B","D","A","F","I","K"], // 333
  ["H","G","B","D","A","F","I","J"], // 334
  ["E","J","B","A","I","D","L","K"], // 335
  ["E","J","B","D","A","H","L","K"], // 336
  ["E","I","B","D","A","H","L","K"], // 337
  ["E","J","B","D","A","H","L","I"], // 338
  ["E","J","B","D","A","H","I","K"], // 339
  ["E","J","B","D","A","G","L","K"], // 340
  ["E","G","B","A","I","D","L","K"], // 341
  ["E","J","B","D","A","G","L","I"], // 342
  ["E","J","B","D","A","G","I","K"], // 343
  ["E","G","B","D","A","H","L","K"], // 344
  ["H","J","B","D","A","G","L","E"], // 345
  ["H","J","B","D","A","G","E","K"], // 346
  ["E","G","B","D","A","H","L","I"], // 347
  ["E","G","B","D","A","H","I","K"], // 348
  ["H","J","B","D","A","G","E","I"], // 349
  ["E","J","B","D","A","F","L","K"], // 350
  ["E","I","B","D","A","F","L","K"], // 351
  ["E","J","B","D","A","F","L","I"], // 352
  ["E","J","B","D","A","F","I","K"], // 353
  ["H","E","B","D","A","F","L","K"], // 354
  ["H","J","B","D","A","F","L","E"], // 355
  ["H","J","B","D","A","F","E","K"], // 356
  ["H","E","B","D","A","F","L","I"], // 357
  ["H","E","B","D","A","F","I","K"], // 358
  ["H","J","B","D","A","F","E","I"], // 359
  ["E","G","B","D","A","F","L","K"], // 360
  ["E","G","B","D","A","F","L","J"], // 361
  ["E","G","B","D","A","F","J","K"], // 362
  ["E","G","B","D","A","F","L","I"], // 363
  ["E","G","B","D","A","F","I","K"], // 364
  ["E","G","B","D","A","F","I","J"], // 365
  ["H","G","B","D","A","F","L","E"], // 366
  ["H","G","B","D","A","F","E","K"], // 367
  ["H","G","B","D","A","F","E","J"], // 368
  ["H","G","B","D","A","F","E","I"], // 369
  ["I","J","B","C","A","H","L","K"], // 370
  ["I","J","B","C","A","G","L","K"], // 371
  ["H","J","B","C","A","G","L","K"], // 372
  ["I","G","B","C","A","H","L","K"], // 373
  ["H","J","B","C","A","G","L","I"], // 374
  ["H","J","B","C","A","G","I","K"], // 375
  ["I","J","B","C","A","F","L","K"], // 376
  ["H","J","B","C","A","F","L","K"], // 377
  ["H","I","B","C","A","F","L","K"], // 378
  ["H","J","B","C","A","F","L","I"], // 379
  ["H","J","B","C","A","F","I","K"], // 380
  ["C","J","B","F","A","G","L","K"], // 381
  ["I","G","B","C","A","F","L","K"], // 382
  ["C","J","B","F","A","G","L","I"], // 383
  ["C","J","B","F","A","G","I","K"], // 384
  ["H","G","B","C","A","F","L","K"], // 385
  ["H","G","B","C","A","F","L","J"], // 386
  ["H","G","B","C","A","F","J","K"], // 387
  ["H","G","B","C","A","F","L","I"], // 388
  ["H","G","B","C","A","F","I","K"], // 389
  ["H","G","B","C","A","F","I","J"], // 390
  ["E","J","B","A","I","C","L","K"], // 391
  ["E","J","B","C","A","H","L","K"], // 392
  ["E","I","B","C","A","H","L","K"], // 393
  ["E","J","B","C","A","H","L","I"], // 394
  ["E","J","B","C","A","H","I","K"], // 395
  ["E","J","B","C","A","G","L","K"], // 396
  ["E","G","B","A","I","C","L","K"], // 397
  ["E","J","B","C","A","G","L","I"], // 398
  ["E","J","B","C","A","G","I","K"], // 399
  ["E","G","B","C","A","H","L","K"], // 400
  ["H","J","B","C","A","G","L","E"], // 401
  ["H","J","B","C","A","G","E","K"], // 402
  ["E","G","B","C","A","H","L","I"], // 403
  ["E","G","B","C","A","H","I","K"], // 404
  ["H","J","B","C","A","G","E","I"], // 405
  ["E","J","B","C","A","F","L","K"], // 406
  ["E","I","B","C","A","F","L","K"], // 407
  ["E","J","B","C","A","F","L","I"], // 408
  ["E","J","B","C","A","F","I","K"], // 409
  ["H","E","B","C","A","F","L","K"], // 410
  ["H","J","B","C","A","F","L","E"], // 411
  ["H","J","B","C","A","F","E","K"], // 412
  ["H","E","B","C","A","F","L","I"], // 413
  ["H","E","B","C","A","F","I","K"], // 414
  ["H","J","B","C","A","F","E","I"], // 415
  ["E","G","B","C","A","F","L","K"], // 416
  ["E","G","B","C","A","F","L","J"], // 417
  ["E","G","B","C","A","F","J","K"], // 418
  ["E","G","B","C","A","F","L","I"], // 419
  ["E","G","B","C","A","F","I","K"], // 420
  ["E","G","B","C","A","F","I","J"], // 421
  ["H","G","B","C","A","F","L","E"], // 422
  ["H","G","B","C","A","F","E","K"], // 423
  ["H","G","B","C","A","F","E","J"], // 424
  ["H","G","B","C","A","F","E","I"], // 425
  ["I","J","B","C","A","D","L","K"], // 426
  ["H","J","B","C","A","D","L","K"], // 427
  ["H","I","B","C","A","D","L","K"], // 428
  ["H","J","B","C","A","D","L","I"], // 429
  ["H","J","B","C","A","D","I","K"], // 430
  ["C","J","B","D","A","G","L","K"], // 431
  ["I","G","B","C","A","D","L","K"], // 432
  ["C","J","B","D","A","G","L","I"], // 433
  ["C","J","B","D","A","G","I","K"], // 434
  ["H","G","B","C","A","D","L","K"], // 435
  ["H","G","B","C","A","D","L","J"], // 436
  ["H","G","B","C","A","D","J","K"], // 437
  ["H","G","B","C","A","D","L","I"], // 438
  ["H","G","B","C","A","D","I","K"], // 439
  ["H","G","B","C","A","D","I","J"], // 440
  ["C","J","B","D","A","F","L","K"], // 441
  ["C","I","B","D","A","F","L","K"], // 442
  ["C","J","B","D","A","F","L","I"], // 443
  ["C","J","B","D","A","F","I","K"], // 444
  ["H","F","B","C","A","D","L","K"], // 445
  ["C","J","B","D","A","F","L","H"], // 446
  ["H","J","B","C","A","F","D","K"], // 447
  ["H","F","B","C","A","D","L","I"], // 448
  ["H","F","B","C","A","D","I","K"], // 449
  ["H","J","B","C","A","F","D","I"], // 450
  ["C","G","B","D","A","F","L","K"], // 451
  ["C","G","B","D","A","F","L","J"], // 452
  ["C","G","B","D","A","F","J","K"], // 453
  ["C","G","B","D","A","F","L","I"], // 454
  ["C","G","B","D","A","F","I","K"], // 455
  ["C","G","B","D","A","F","I","J"], // 456
  ["C","G","B","D","A","F","L","H"], // 457
  ["H","G","B","C","A","F","D","K"], // 458
  ["H","G","B","C","A","F","D","J"], // 459
  ["H","G","B","C","A","F","D","I"], // 460
  ["E","J","B","C","A","D","L","K"], // 461
  ["E","I","B","C","A","D","L","K"], // 462
  ["E","J","B","C","A","D","L","I"], // 463
  ["E","J","B","C","A","D","I","K"], // 464
  ["H","E","B","C","A","D","L","K"], // 465
  ["H","J","B","C","A","D","L","E"], // 466
  ["H","J","B","C","A","D","E","K"], // 467
  ["H","E","B","C","A","D","L","I"], // 468
  ["H","E","B","C","A","D","I","K"], // 469
  ["H","J","B","C","A","D","E","I"], // 470
  ["E","G","B","C","A","D","L","K"], // 471
  ["E","G","B","C","A","D","L","J"], // 472
  ["E","G","B","C","A","D","J","K"], // 473
  ["E","G","B","C","A","D","L","I"], // 474
  ["E","G","B","C","A","D","I","K"], // 475
  ["E","G","B","C","A","D","I","J"], // 476
  ["H","G","B","C","A","D","L","E"], // 477
  ["H","G","B","C","A","D","E","K"], // 478
  ["H","G","B","C","A","D","E","J"], // 479
  ["H","G","B","C","A","D","E","I"], // 480
  ["C","E","B","D","A","F","L","K"], // 481
  ["C","J","B","D","A","F","L","E"], // 482
  ["C","J","B","D","A","F","E","K"], // 483
  ["C","E","B","D","A","F","L","I"], // 484
  ["C","E","B","D","A","F","I","K"], // 485
  ["C","J","B","D","A","F","E","I"], // 486
  ["H","F","B","C","A","D","L","E"], // 487
  ["H","E","B","C","A","F","D","K"], // 488
  ["H","J","B","C","A","F","D","E"], // 489
  ["H","E","B","C","A","F","D","I"], // 490
  ["C","G","B","D","A","F","L","E"], // 491
  ["C","G","B","D","A","F","E","K"], // 492
  ["C","G","B","D","A","F","E","J"], // 493
  ["C","G","B","D","A","F","E","I"], // 494
  ["H","G","B","C","A","F","D","E"], // 495
];

// Construye el mapa { "GRUPOS_ORDENADOS": { matchNum: groupId } } a partir de la
// tabla oficial. Como hay exactamente C(12,8)=495 combinaciones únicas de 8 grupos,
// cada conjunto ordenado de grupos es una clave única.
const THIRD_PLACE_SCENARIOS = THIRD_PLACE_RAW.reduce((acc, row) => {
  const assignments = {};
  THIRD_PLACE_FIRST_ORDER.forEach((first, idx) => {
    assignments[THIRD_PLACE_MATCH_BY_FIRST[first]] = row[idx];
  });
  const key = [...row].sort().join("");
  acc[key] = assignments;
  return acc;
}, {});

// Grupos posibles por partido, derivados de la tabla oficial de terceros.
// Se usa solo como respaldo del backtracking si una combinación no estuviera mapeada.
const MATCH_POSSIBLE_GROUPS = (() => {
  const map = {};
  Object.values(THIRD_PLACE_SCENARIOS).forEach((assignments) => {
    Object.entries(assignments).forEach(([matchNum, group]) => {
      if (!map[matchNum]) map[matchNum] = new Set();
      map[matchNum].add(group);
    });
  });
  Object.keys(map).forEach((m) => { map[m] = Array.from(map[m]).sort(); });
  return map;
})();

/**
 * Genera un escenario válido dinámicamente si no existe en la tabla.
 * Usa backtracking para encontrar una asignación válida.
 */
function generateScenario(qualifiedGroups) {
  const sortedGroups = [...qualifiedGroups].sort();
  const assignments = {};
  const usedGroups = new Set();
  const matchOrder = [74, 77, 79, 80, 81, 82, 85, 87];

  function backtrack(matchIdx) {
    if (matchIdx === matchOrder.length) return true;
    const matchNum = matchOrder[matchIdx];
    const possibleGroups = MATCH_POSSIBLE_GROUPS[matchNum];

    for (const group of sortedGroups) {
      if (!usedGroups.has(group) && possibleGroups.includes(group)) {
        assignments[matchNum] = group;
        usedGroups.add(group);
        if (backtrack(matchIdx + 1)) return true;
        usedGroups.delete(group);
        delete assignments[matchNum];
      }
    }
    return false;
  }

  if (backtrack(0)) {
    return { scenarioKey: sortedGroups.join(""), assignments };
  }
  return null;
}

/**
 * Rankea los 12 terceros lugares y selecciona los 8 mejores.
 */
function rankThirdPlaces(groupsState, TABS) {
  const thirdPlaces = [];

  TABS.forEach((groupId) => {
    const standings = computeStandings(groupsState[groupId], groupId);
    if (standings[2]) {
      thirdPlaces.push({ ...standings[2], groupId });
    }
  });

  const groupSeed = hashString(TABS.join(''));

  thirdPlaces.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.diff !== a.diff) return b.diff - a.diff;
    if (b.gf !== a.gf) return b.gf - a.gf;
    if (a.fairPlay !== b.fairPlay) return a.fairPlay - b.fairPlay;
    return seededRandom(groupSeed + hashString(b.team)) - seededRandom(groupSeed + hashString(a.team));
  });

  return thirdPlaces;
}

/**
 * Obtiene los 8 mejores terceros con su asignación de partido según escenarios FIFA.
 */
export function getBestThirdPlaces(groupsState, TABS) {
  const allThirds = rankThirdPlaces(groupsState, TABS);
  const top8Thirds = allThirds.slice(0, 8);
  const qualifiedGroups = top8Thirds.map(t => t.groupId);
  const scenarioKey = [...qualifiedGroups].sort().join("");

  // Buscar en tabla predefinida o generar dinámicamente
  let scenario;
  const predefined = THIRD_PLACE_SCENARIOS[scenarioKey];
  if (predefined) {
    // Validar que no haya grupos duplicados en la asignación
    const assignedGroups = Object.values(predefined);
    const uniqueGroups = new Set(assignedGroups);
    if (uniqueGroups.size === assignedGroups.length) {
      scenario = { scenarioKey, assignments: predefined };
    } else {
      // Escenario predefinido tiene duplicados, regenerar dinámicamente
      console.warn(`Escenario predefinido ${scenarioKey} tiene grupos duplicados, regenerando...`);
      scenario = generateScenario(qualifiedGroups);
    }
  } else {
    scenario = generateScenario(qualifiedGroups);
  }

  if (!scenario) {
    console.error(`No se pudo resolver escenario: ${scenarioKey}`);
    return top8Thirds;
  }

  // Crear mapa de terceros por grupo
  const thirdsByGroup = {};
  top8Thirds.forEach(t => { thirdsByGroup[t.groupId] = t; });

  // Crear mapa de terceros por partido
  const thirdsByMatch = {};
  for (const [matchNum, groupId] of Object.entries(scenario.assignments)) {
    thirdsByMatch[matchNum] = thirdsByGroup[groupId];
  }

  return {
    rankedThirds: top8Thirds,
    scenario,
    thirdsByMatch,
    getThirdForMatch: (matchNum) => thirdsByMatch[matchNum] || null,
  };
}

// Simula todos los partidos de fase de grupos con resultados aleatorios
// NO sobrescribe partidos con locked, resultado oficial, o scores ingresados manualmente por el usuario
export function simulateGroupStage(groupsState) {
  const simulatedGroups = {};

  Object.keys(groupsState).forEach((groupId) => {
    const group = groupsState[groupId];
    const simulatedDays = group.days.map((day) => ({
      ...day,
      matches: day.matches.map((match) => {
        // Respetar partidos bloqueados o con resultado oficial
        if (match.locked || OFFICIAL_GROUP_RESULTS[match.id]) return match;
        // Respetar partidos editados manualmente por el usuario (no simulados)
        const hasScores = match.homeScore !== "" && match.homeScore != null && match.awayScore !== "" && match.awayScore != null;
        if (hasScores && !match.simulated) return match;
        // Simular con probabilidad ponderada por fuerza del equipo
        const { homeScore, awayScore } = simulateMatchScore(match.home, match.away);
        return { ...match, homeScore, awayScore, simulated: true };
      }),
    }));
    simulatedGroups[groupId] = markGroupAsSimulated({ ...group, days: simulatedDays }, "auto");
  });

  return simulatedGroups;
}

// Simula solo los partidos de un grupo específico
// NO sobrescribe partidos con locked, resultado oficial, o scores ingresados manualmente por el usuario
export function simulateSingleGroup(group) {
  const simulatedDays = group.days.map((day) => ({
    ...day,
    matches: day.matches.map((match) => {
      // Respetar partidos bloqueados o con resultado oficial
      if (match.locked || OFFICIAL_GROUP_RESULTS[match.id]) return match;
      // Respetar partidos editados manualmente por el usuario (no simulados)
      const hasScores = match.homeScore !== "" && match.homeScore != null && match.awayScore !== "" && match.awayScore != null;
      if (hasScores && !match.simulated) return match;
      // Simular con probabilidad ponderada por fuerza del equipo
      const { homeScore, awayScore } = simulateMatchScore(match.home, match.away);
      return { ...match, homeScore, awayScore, simulated: true };
    }),
  }));

  return markGroupAsSimulated({ ...group, days: simulatedDays }, "manual");
}

// Verifica si todos los grupos tienen todos sus partidos con resultados completos
export function areAllGroupsComplete(groupsState, TABS) {
  return TABS.every((groupId) => {
    const group = groupsState[groupId];
    if (!group || !group.days) return false;

    return group.days.every((day) =>
      day.matches.every((match) => {
        const homeScore = match.homeScore;
        const awayScore = match.awayScore;
        // Un partido está completo si ambos scores no están vacíos
        return homeScore !== "" && homeScore !== null && homeScore !== undefined &&
          awayScore !== "" && awayScore !== null && awayScore !== undefined;
      })
    );
  });
}

/**
 * Resuelve los datos de visualización de un equipo a partir de su nombre.
 *
 * Maneja tres casos:
 * 1. Nombre vacío o "TBD": el slot aún no tiene equipo asignado.
 * 2. Referencia dinámica (GP<n> / PP<n>): el equipo se conocerá cuando se
 *    resuelva el partido indicado (Ganador Partido / Perdedor Partido).
 * 3. Nombre real: busca el código de bandera en `teamFlagMap`; si no existe,
 *    usa los primeros 3 caracteres del nombre en mayúsculas como fallback.
 */
export const getTeamDisplay = (teamName) => {
  if (!teamName || teamName === "TBD") {
    return {
      display: "PAÍS",
      flag: "default",
      isPlaceholder: true,
    };
  }
  // Detectar referencias GP (Ganador Partido) o PP (Perdedor Partido)
  const isReference = /^(GP|PP)\d+$/.test(teamName);
  if (isReference) {
    return {
      display: teamName,
      flag: "PAÍS",
      isPlaceholder: true,
    };
  }
  const trimmed = teamName.trim();
  return {
    display: trimmed,
    flag: teamFlagMap[trimmed] || trimmed.substring(0, 3).toUpperCase(),
    isPlaceholder: false,
  };
}

export const TABS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

export const generateQualifyingMatchesFromGroups = (groups) => {
  const qualified = {};
  let hasAnyScore = false;

  TABS.forEach((groupId) => {
    const groupStandings = computeStandings(groups[groupId], groupId);
    qualified[groupId] = {
      first: groupStandings[0],
      second: groupStandings[1],
      third: groupStandings[2],
    };

    const group = groups[groupId];
    if (group && group.days) {
      group.days.forEach((day) => {
        day.matches.forEach((match) => {
          if (match.homeScore !== "" || match.awayScore !== "") {
            hasAnyScore = true;
          }
        });
      });
    }
  });

  const thirdsResult = getBestThirdPlaces(groups, TABS);

  const createKnockoutMatch = (id, matchNumber, home, away, dateUTC, venue) => {
    const official = getOfficialResult(matchNumber);
    return {
      id, matchNumber,
      home: hasAnyScore ? (home || "TBD") : "TBD",
      away: hasAnyScore ? (away || "TBD") : "TBD",
      dateUTC, venue,
      winner: official?.winner || null,
      homeScore: official?.homeScore ?? null,
      awayScore: official?.awayScore ?? null,
      locked: !!official,
    };
  };

  const getThirdForMatch = (matchNum) => {
    if (!thirdsResult.getThirdForMatch) return "3°";
    const third = thirdsResult.getThirdForMatch(matchNum);
    return third?.team || "3°";
  };

  // Fechas en ISO 8601 (UTC). Las horas originales son CDT (UTC-6).
  // Para convertir: hora_MX + 6h = UTC
  const roundOf32 = [
    createKnockoutMatch("r32-73", 73, qualified.A.second?.team, qualified.B.second?.team, "2026-06-28T19:00:00Z", "Los Angeles Stadium, Los Ángeles"),
    createKnockoutMatch("r32-74", 74, qualified.E.first?.team, getThirdForMatch(74), "2026-06-29T20:30:00Z", "Boston Stadium, Boston"),
    createKnockoutMatch("r32-75", 75, qualified.F.first?.team, qualified.C.second?.team, "2026-06-30T01:00:00Z", "Monterrey Stadium, Monterrey"),
    createKnockoutMatch("r32-76", 76, qualified.C.first?.team, qualified.F.second?.team, "2026-06-29T17:00:00Z", "Houston Stadium, Houston"),
    createKnockoutMatch("r32-77", 77, qualified.I.first?.team, getThirdForMatch(77), "2026-06-30T21:00:00Z", "New York New Jersey Stadium, Nueva York"),
    createKnockoutMatch("r32-78", 78, qualified.E.second?.team, qualified.I.second?.team, "2026-06-30T17:00:00Z", "Dallas Stadium, Dallas"),
    createKnockoutMatch("r32-79", 79, qualified.A.first?.team, getThirdForMatch(79), "2026-07-01T01:00:00Z", "Mexico City Stadium, Ciudad de México"),
    createKnockoutMatch("r32-80", 80, qualified.L.first?.team, getThirdForMatch(80), "2026-07-01T16:00:00Z", "Atlanta Stadium, Atlanta"),
    createKnockoutMatch("r32-81", 81, qualified.D.first?.team, getThirdForMatch(81), "2026-07-02T00:00:00Z", "San Francisco Bay Area Stadium, San Francisco"),
    createKnockoutMatch("r32-82", 82, qualified.G.first?.team, getThirdForMatch(82), "2026-07-01T20:00:00Z", "Seattle Stadium, Seattle"),
    
    createKnockoutMatch("r32-83", 84, qualified.K.second?.team, qualified.L.second?.team, "2026-07-02T23:00:00Z", "Los Angeles Stadium, Los Ángeles"),
    createKnockoutMatch("r32-84", 83, qualified.H.first?.team, qualified.J.second?.team, "2026-07-02T19:00:00Z", "Toronto Stadium, Toronto"),
    
    createKnockoutMatch("r32-85", 85, qualified.B.first?.team, getThirdForMatch(85), "2026-07-03T03:00:00Z", "BC Place Vancouver, Vancouver"),

    createKnockoutMatch("r32-86", 88, qualified.J.first?.team, qualified.H.second?.team, "2026-07-03T22:00:00Z", "Miami Stadium, Miami"),
    createKnockoutMatch("r32-87", 87, qualified.K.first?.team, getThirdForMatch(87), "2026-07-04T01:30:00Z", "Kansas City Stadium, Kansas City"),
    createKnockoutMatch("r32-88", 86, qualified.D.second?.team, qualified.G.second?.team, "2026-07-03T18:00:00Z", "Dallas Stadium, Dallas"),
  ];

  const getWinnerOrRef = (match, prefix = "GP") => {
    if (!match) return "TBD";
    if (match.winner) return match.winner;
    return `${prefix}${match.matchNumber}`;
  };

  const getLoserOrRef = (match, prefix = "PP") => {
    if (!match) return "TBD";
    if (match.winner) return match.winner === match.home ? match.away : match.home;
    return `${prefix}${match.matchNumber}`;
  };

  // Helper para aplicar resultado oficial a un partido de ronda
  const applyOfficial = (matchNumber) => {
    const o = getOfficialResult(matchNumber);
    if (!o) return { winner: null, homeScore: null, awayScore: null, locked: false };
    return { winner: o.winner || null, homeScore: o.homeScore ?? null, awayScore: o.awayScore ?? null, locked: true };
  };

  const roundOf16 = [
    { id: "r16-89", matchNumber: 89, home: getWinnerOrRef(roundOf32[1]), away: getWinnerOrRef(roundOf32[4]), dateUTC: "2026-07-04T21:00:00Z", venue: "Philadelphia Stadium, Filadelfia", ...applyOfficial(89) },
    { id: "r16-90", matchNumber: 90, home: getWinnerOrRef(roundOf32[0]), away: getWinnerOrRef(roundOf32[2]), dateUTC: "2026-07-04T17:00:00Z", venue: "Houston Stadium, Houston", ...applyOfficial(90) },
    { id: "r16-91", matchNumber: 91, home: getWinnerOrRef(roundOf32[3]), away: getWinnerOrRef(roundOf32[5]), dateUTC: "2026-07-05T20:00:00Z", venue: "New York New Jersey Stadium, Nueva York", ...applyOfficial(91) },
    { id: "r16-92", matchNumber: 92, home: getWinnerOrRef(roundOf32[6]), away: getWinnerOrRef(roundOf32[7]), dateUTC: "2026-07-06T00:00:00Z", venue: "Mexico City Stadium, Ciudad de México", ...applyOfficial(92) },
    { id: "r16-93", matchNumber: 93, home: getWinnerOrRef(roundOf32[11]), away: getWinnerOrRef(roundOf32[10]), dateUTC: "2026-07-06T19:00:00Z", venue: "Dallas Stadium, Dallas", ...applyOfficial(93) },
    { id: "r16-94", matchNumber: 94, home: getWinnerOrRef(roundOf32[8]), away: getWinnerOrRef(roundOf32[9]), dateUTC: "2026-07-07T00:00:00Z", venue: "Seattle Stadium, Seattle", ...applyOfficial(94) },
    { id: "r16-95", matchNumber: 95, home: getWinnerOrRef(roundOf32[13]), away: getWinnerOrRef(roundOf32[15]), dateUTC: "2026-07-07T16:00:00Z", venue: "Atlanta Stadium, Atlanta", ...applyOfficial(95) },
    { id: "r16-96", matchNumber: 96, home: getWinnerOrRef(roundOf32[12]), away: getWinnerOrRef(roundOf32[14]), dateUTC: "2026-07-07T20:00:00Z", venue: "BC Place Vancouver, Vancouver", ...applyOfficial(96) },
  ];

  const roundOf8 = [
    { id: "r8-97", matchNumber: 97, home: getWinnerOrRef(roundOf16[0]), away: getWinnerOrRef(roundOf16[1]), dateUTC: "2026-07-09T20:00:00Z", venue: "Boston Stadium, Boston", ...applyOfficial(97) },
    { id: "r8-98", matchNumber: 98, home: getWinnerOrRef(roundOf16[4]), away: getWinnerOrRef(roundOf16[5]), dateUTC: "2026-07-10T19:00:00Z", venue: "Los Angeles Stadium, Los Ángeles", ...applyOfficial(98) },
    { id: "r8-99", matchNumber: 99, home: getWinnerOrRef(roundOf16[2]), away: getWinnerOrRef(roundOf16[3]), dateUTC: "2026-07-11T21:00:00Z", venue: "Miami Stadium, Miami", ...applyOfficial(99) },
    { id: "r8-100", matchNumber: 100, home: getWinnerOrRef(roundOf16[6]), away: getWinnerOrRef(roundOf16[7]), dateUTC: "2026-07-12T01:00:00Z", venue: "Kansas City Stadium, Kansas City", ...applyOfficial(100) },
  ];

  const semiFinals = [
    { id: "sf-101", matchNumber: 101, home: getWinnerOrRef(roundOf8[0]), away: getWinnerOrRef(roundOf8[1]), dateUTC: "2026-07-14T19:00:00Z", venue: "Dallas Stadium, Dallas", ...applyOfficial(101) },
    { id: "sf-102", matchNumber: 102, home: getWinnerOrRef(roundOf8[2]), away: getWinnerOrRef(roundOf8[3]), dateUTC: "2026-07-15T19:00:00Z", venue: "Atlanta Stadium, Atlanta", ...applyOfficial(102) },
  ];

  const thirdPlace = [{
    id: "third-103", matchNumber: 103, home: getLoserOrRef(semiFinals[0]), away: getLoserOrRef(semiFinals[1]), dateUTC: "2026-07-18T21:00:00Z", venue: "Miami Stadium, Miami", ...applyOfficial(103),
  }];

  const final = [{
    id: "final-104", matchNumber: 104, home: getWinnerOrRef(semiFinals[0]), away: getWinnerOrRef(semiFinals[1]), dateUTC: "2026-07-19T19:00:00Z", venue: "New York New Jersey Stadium, Nueva York", ...applyOfficial(104),
  }];

  return { roundOf32, roundOf16, roundOf8, semiFinals, final, thirdPlace };
};


// Simula todas las rondas eliminatorias con resultados aleatorios
// NO sobrescribe partidos con locked: true
// Respeta la propagación de ganadores ronda por ronda
export function simulateQualifyingRounds(qualifyingState) {
  const simulated = {};

  // Helper para elegir ganador ponderado por fuerza del equipo
  const pickWinner = (match) => {
    if (match.locked && match.winner) return match.winner;
    if (!match.home || !match.away || match.home === "TBD" || match.away === "TBD") return null;
    if (/^(GP|PP)\d+$/.test(match.home) || /^(GP|PP)\d+$/.test(match.away)) return null;
    const prob = getWinProbability(match.home, match.away);
    return Math.random() < prob ? match.home : match.away;
  };

  const generateScore = (match) => {
    if (!match.home || !match.away || match.home === "TBD" || match.away === "TBD") {
      return { homeScore: String(Math.floor(Math.random() * 4)), awayScore: String(Math.floor(Math.random() * 4)) };
    }
    return simulateMatchScore(match.home, match.away);
  };

  // Simular una ronda completa
  const simulateRound = (matches) => {
    if (!matches || matches.length === 0) return matches || [];
    return matches.map((match) => {
      if (match.locked) return match;
      const winner = pickWinner(match);
      if (!winner) return match;
      let { homeScore, awayScore } = generateScore(match);
      if (winner === match.home && Number(homeScore) <= Number(awayScore)) {
        homeScore = String(Number(awayScore) + 1);
      } else if (winner === match.away && Number(awayScore) <= Number(homeScore)) {
        awayScore = String(Number(homeScore) + 1);
      }
      return { ...match, winner, homeScore, awayScore };
    });
  };

  // Helper para obtener ganador o referencia
  const getWinnerOrRef = (match) => {
    if (!match) return "TBD";
    if (match.winner) return match.winner;
    return `GP${match.matchNumber}`;
  };

  const getLoserOrRef = (match) => {
    if (!match) return "TBD";
    if (match.winner) return match.winner === match.home ? match.away : match.home;
    return `PP${match.matchNumber}`;
  };

  // Helper para preservar resultado oficial
  const applyOfficial = (matchNumber, existingMatch) => {
    const o = getOfficialResult(matchNumber);
    if (o) return { winner: o.winner || null, homeScore: o.homeScore ?? null, awayScore: o.awayScore ?? null, locked: true };
    return { winner: existingMatch?.winner || null, homeScore: existingMatch?.homeScore ?? null, awayScore: existingMatch?.awayScore ?? null, locked: false };
  };

  // 1. Simular Dieciseisavos (Round of 32)
  simulated.roundOf32 = simulateRound(qualifyingState.roundOf32);

  // 2. Actualizar y simular Octavos (Round of 16)
  if (simulated.roundOf32 && simulated.roundOf32.length >= 16) {
    const getR32 = (matchNum) => simulated.roundOf32.find(m => m.matchNumber === matchNum);
    simulated.roundOf16 = [
      { ...(qualifyingState.roundOf16?.[0] || {}), id: "r16-89", matchNumber: 89, home: getWinnerOrRef(getR32(74)), away: getWinnerOrRef(getR32(77)), ...applyOfficial(89, qualifyingState.roundOf16?.[0]) },
      { ...(qualifyingState.roundOf16?.[1] || {}), id: "r16-90", matchNumber: 90, home: getWinnerOrRef(getR32(73)), away: getWinnerOrRef(getR32(75)), ...applyOfficial(90, qualifyingState.roundOf16?.[1]) },
      { ...(qualifyingState.roundOf16?.[2] || {}), id: "r16-91", matchNumber: 91, home: getWinnerOrRef(getR32(76)), away: getWinnerOrRef(getR32(78)), ...applyOfficial(91, qualifyingState.roundOf16?.[2]) },
      { ...(qualifyingState.roundOf16?.[3] || {}), id: "r16-92", matchNumber: 92, home: getWinnerOrRef(getR32(79)), away: getWinnerOrRef(getR32(80)), ...applyOfficial(92, qualifyingState.roundOf16?.[3]) },
      { ...(qualifyingState.roundOf16?.[4] || {}), id: "r16-93", matchNumber: 93, home: getWinnerOrRef(getR32(83)), away: getWinnerOrRef(getR32(84)), ...applyOfficial(93, qualifyingState.roundOf16?.[4]) },
      { ...(qualifyingState.roundOf16?.[5] || {}), id: "r16-94", matchNumber: 94, home: getWinnerOrRef(getR32(81)), away: getWinnerOrRef(getR32(82)), ...applyOfficial(94, qualifyingState.roundOf16?.[5]) },
      { ...(qualifyingState.roundOf16?.[6] || {}), id: "r16-95", matchNumber: 95, home: getWinnerOrRef(getR32(86)), away: getWinnerOrRef(getR32(88)), ...applyOfficial(95, qualifyingState.roundOf16?.[6]) },
      { ...(qualifyingState.roundOf16?.[7] || {}), id: "r16-96", matchNumber: 96, home: getWinnerOrRef(getR32(85)), away: getWinnerOrRef(getR32(87)), ...applyOfficial(96, qualifyingState.roundOf16?.[7]) },
    ];
    simulated.roundOf16 = simulateRound(simulated.roundOf16);
  } else {
    simulated.roundOf16 = qualifyingState.roundOf16 || [];
  }

  // 3. Actualizar y simular Cuartos (Round of 8)
  if (simulated.roundOf16 && simulated.roundOf16.length >= 8) {
    const getR16 = (matchNum) => simulated.roundOf16.find(m => m.matchNumber === matchNum);
    simulated.roundOf8 = [
      { ...(qualifyingState.roundOf8?.[0] || {}), id: "r8-97", matchNumber: 97, home: getWinnerOrRef(getR16(89)), away: getWinnerOrRef(getR16(90)), ...applyOfficial(97, qualifyingState.roundOf8?.[0]) },
      { ...(qualifyingState.roundOf8?.[1] || {}), id: "r8-98", matchNumber: 98, home: getWinnerOrRef(getR16(93)), away: getWinnerOrRef(getR16(94)), ...applyOfficial(98, qualifyingState.roundOf8?.[1]) },
      { ...(qualifyingState.roundOf8?.[2] || {}), id: "r8-99", matchNumber: 99, home: getWinnerOrRef(getR16(91)), away: getWinnerOrRef(getR16(92)), ...applyOfficial(99, qualifyingState.roundOf8?.[2]) },
      { ...(qualifyingState.roundOf8?.[3] || {}), id: "r8-100", matchNumber: 100, home: getWinnerOrRef(getR16(95)), away: getWinnerOrRef(getR16(96)), ...applyOfficial(100, qualifyingState.roundOf8?.[3]) },
    ];
    simulated.roundOf8 = simulateRound(simulated.roundOf8);
  } else {
    simulated.roundOf8 = qualifyingState.roundOf8 || [];
  }

  // 4. Actualizar y simular Semifinales
  if (simulated.roundOf8 && simulated.roundOf8.length >= 4) {
    const getR8 = (matchNum) => simulated.roundOf8.find(m => m.matchNumber === matchNum);
    simulated.semiFinals = [
      { ...(qualifyingState.semiFinals?.[0] || {}), id: "sf-101", matchNumber: 101, home: getWinnerOrRef(getR8(97)), away: getWinnerOrRef(getR8(98)), ...applyOfficial(101, qualifyingState.semiFinals?.[0]) },
      { ...(qualifyingState.semiFinals?.[1] || {}), id: "sf-102", matchNumber: 102, home: getWinnerOrRef(getR8(99)), away: getWinnerOrRef(getR8(100)), ...applyOfficial(102, qualifyingState.semiFinals?.[1]) },
    ];
    simulated.semiFinals = simulateRound(simulated.semiFinals);
  } else {
    simulated.semiFinals = qualifyingState.semiFinals || [];
  }

  // 5. Actualizar y simular Final y Tercer Lugar
  if (simulated.semiFinals && simulated.semiFinals.length >= 2) {
    const getSF = (matchNum) => simulated.semiFinals.find(m => m.matchNumber === matchNum);

    simulated.thirdPlace = [{
      ...(qualifyingState.thirdPlace?.[0] || {}),
      id: "third-103", matchNumber: 103,
      home: getLoserOrRef(getSF(101)),
      away: getLoserOrRef(getSF(102)),
      ...applyOfficial(103, qualifyingState.thirdPlace?.[0]),
    }];
    simulated.thirdPlace = simulateRound(simulated.thirdPlace);

    simulated.final = [{
      ...(qualifyingState.final?.[0] || {}),
      id: "final-104", matchNumber: 104,
      home: getWinnerOrRef(getSF(101)),
      away: getWinnerOrRef(getSF(102)),
      ...applyOfficial(104, qualifyingState.final?.[0]),
    }];
    simulated.final = simulateRound(simulated.final);
  } else {
    simulated.final = qualifyingState.final || [];
    simulated.thirdPlace = qualifyingState.thirdPlace || [];
  }

  return simulated;
}